/**
 * Collection cards for Created-by / Owned frozen artworks.
 *
 * Cards are metadata-only (the backend collection projection excludes content —
 * no duplicate content authority). Clicking a card opens the read-only Frozen
 * Artwork detail (#artwork/<id>), which fetches the canonical payload.
 */
import type { ArtworkPublic } from "../../lib/artwork-api";

function shortHash(h: string): string {
  return h && h.length > 14 ? `${h.slice(0, 10)}…${h.slice(-4)}` : h;
}

export function ArtworkCard({ art }: { art: ArtworkPublic }) {
  return (
    <a
      href={`#artwork/${art.artwork_id}`}
      data-testid="artwork-card"
      data-artwork-id={art.artwork_id}
      className="group flex flex-col gap-2 rounded-xl border border-white/10 bg-white/[0.03] p-3 no-underline transition hover:border-teal-300/40 hover:bg-white/[0.05]"
    >
      <div className="flex items-center justify-between">
        <span className="truncate font-medium text-white" title={art.title}>
          {art.title || "Untitled"}
        </span>
        <span className="shrink-0 rounded-full border border-teal-300/40 bg-teal-400/10 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.14em] text-teal-200">
          {art.status}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-1 font-mono text-[10px] text-white/50">
        <span>{art.width}×{art.height}</span>
        <span className="text-right">{art.footprint} cells</span>
        <span>royalty {art.royalty_percent}%</span>
        <span className="text-right">{new Date(art.frozen_at).toLocaleDateString()}</span>
      </div>
      <div className="truncate font-mono text-[9px] text-white/30" title={art.content_hash}>
        {shortHash(art.content_hash)}
      </div>
    </a>
  );
}

export function CollectionGrid({
  items,
  emptyTestId,
  emptyText,
  gridTestId,
}: {
  items: ArtworkPublic[];
  emptyTestId: string;
  emptyText: string;
  gridTestId: string;
}) {
  if (items.length === 0) {
    return (
      <div data-testid={emptyTestId} className="rounded-xl border border-dashed border-white/10 p-8 text-center text-sm text-white/45">
        {emptyText}
      </div>
    );
  }
  return (
    <div data-testid={gridTestId} className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {items.map((a) => (
        <ArtworkCard key={a.artwork_id} art={a} />
      ))}
    </div>
  );
}
